import { zodResolver } from '@hookform/resolvers/zod';
import {
  ApiError,
  CatalogueItemsController_create_v1,
  type CatalogueItemsController_create_v1Body,
  type OutletResource,
  type OwnerCatalogueItemResource,
} from '@repo/api-client';
import { Alert, AlertDescription } from '@repo/ui/components/alert';
import { Button } from '@repo/ui/components/button';
import { Checkbox } from '@repo/ui/components/checkbox';
import {
  Dialog,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@repo/ui/components/dialog';
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@repo/ui/components/form';
import { Input } from '@repo/ui/components/input';
import { Label } from '@repo/ui/components/label';
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@repo/ui/components/select';
import { Switch } from '@repo/ui/components/switch';
import { IconAlertTriangle } from '@tabler/icons-react';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';

import { useApiClient } from '@/api/api-client';
import { messageForFailure } from '@/api/failure';
import {
  barcodeOnTheWire,
  catalogueCodeSchema,
  catalogueNameSchema,
  optionalBarcodeSchema,
  priceInRupeesSchema,
} from '@/lib/field-schemas';
import { IDEMPOTENCY_KEY_HEADER, newIdempotencyKey } from '@/lib/idempotency-key';
import { paiseFromRupees } from '@/lib/money';

import { fieldLabel, fieldSurface, FormDialogContent } from '../auth/AuthFrame';
import { barcodeRefusal } from './barcode-refusal';
import { BarcodeField } from './BarcodeField';
import {
  CATALOGUE_KIND_ITEMS,
  CATALOGUE_KINDS,
  type CatalogueKind,
  hintForKind,
  labelForKind,
  mayTrackStock,
} from './catalogue-kinds';
import { taxFields, taxOnTheWire, withTaxRule } from './tax';
import { TaxFields } from './TaxFields';

/**
 * Creating a catalogue item: `POST /v1/catalogue/items`, and the four refusals
 * it can answer with.
 *
 * ## Two fields that cannot be taken back
 *
 * **`kind` is immutable.** `UpdateCatalogueItemDto` has no field for it, so
 * there is no route that changes one — getting it wrong means retiring the item
 * and creating it again under a *different* code, because the first code is gone
 * for good. The picker says so at the field rather than in a paragraph at the
 * top, which is where somebody about to change it is looking.
 *
 * **`code` is unique for all time, including across retirement.** This is the
 * opposite of an outlet code, which comes back when its outlet is retired, and
 * the difference is not a detail: two total unique indexes on `(kind, code)` in
 * the API say so, because everything here lands in money history that Reporting
 * groups by code. So `CATALOGUE_CODE_TAKEN` is worded as *spent* rather than as
 * in use, and there is no "retire the item holding it" way out to offer.
 *
 * ## The stock switch is disabled rather than refused
 *
 * Only a `PRODUCT` may count stock (FR-212); a ticket type and an activity are
 * never counted, and the API answers `CATALOGUE_STOCK_ON_NON_PRODUCT` (422) to
 * anything else. `docs/ui-conventions.md` asks that a control the caller cannot
 * use be unavailable rather than shown and refused, so the switch goes disabled
 * with the kind — and the value is forced back to `false` at the same moment, so
 * a switch turned on as a product and then changed to a ticket type cannot send
 * a `true` nobody can see.
 *
 * ## The outlets
 *
 * `outletIds` is a convenience for the same set FE023 manages afterwards, and
 * naming none is legitimate — an item available nowhere yet is a normal state.
 * If the item tracks stock and any outlet named carries none, the **whole create
 * is refused** and nothing is written: `CATALOGUE_STOCK_NOT_AVAILABLE_HERE`, a
 * 409. See `handleSubmit` for why this screen names the offending outlet itself.
 */

const newCatalogueItemSchema = withTaxRule(
  z.object({
    kind: z.custom<CatalogueKind>(),
    code: catalogueCodeSchema,
    name: catalogueNameSchema,
    barcode: optionalBarcodeSchema,
    tracksStock: z.boolean(),
    sellingRupees: priceInRupeesSchema,
    costRupees: priceInRupeesSchema,
    ...taxFields,
    outletIds: z.array(z.string()),
  }),
);

type NewCatalogueItemValues = z.infer<typeof newCatalogueItemSchema>;

export function NewCatalogueItemDialog({
  open,
  onOpenChange,
  outlets,
  onCreated,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;

  /**
   * Every outlet the park has, already read by the screen behind this.
   *
   * Passed in rather than fetched here so the dialog opens instantly and so the
   * `carriesStock` flag this form reasons about is the same copy the screen's
   * own outlet filter is using. Empty is a real state — a park before its first
   * outlet — and the picker says so rather than rendering nothing.
   */
  outlets: readonly OutletResource[];

  onCreated: (item: OwnerCatalogueItemResource) => void;
}) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <FormDialogContent>
        <DialogHeader>
          <DialogTitle>New catalogue item</DialogTitle>
          <DialogDescription>
            Something the garden sells. The kind and the code are both fixed once this is created,
            and neither can be changed afterwards.
          </DialogDescription>
        </DialogHeader>

        {/* The form is a child so that closing the dialog unmounts it and the
            next open starts empty — and so the idempotency key below belongs to
            one attempt at creating one item rather than to the screen. */}
        <NewCatalogueItemForm outlets={outlets} onCreated={onCreated} />
      </FormDialogContent>
    </Dialog>
  );
}

function NewCatalogueItemForm({
  outlets,
  onCreated,
}: {
  outlets: readonly OutletResource[];
  onCreated: (item: OwnerCatalogueItemResource) => void;
}) {
  const client = useApiClient();

  const form = useForm({
    resolver: zodResolver(newCatalogueItemSchema),
    mode: 'onTouched',
    defaultValues: {
      kind: 'PRODUCT' as CatalogueKind,
      code: '',
      name: '',
      barcode: '',
      tracksStock: false,
      sellingRupees: '',
      costRupees: '',
      chargesGst: false,
      hsnCode: '',
      taxRate: '',
      outletIds: [] as string[],
    },
  });

  const [formError, setFormError] = useState<string | null>(null);

  // One key for as long as this form is open, which is exactly one attempt at
  // creating one item: the dialog unmounts on success, so the next item gets a
  // fresh one. A refusal rolls the claim back server-side, so correcting a taken
  // code and submitting again re-claims cleanly rather than colliding with
  // itself.
  const [idempotencyKey] = useState(newIdempotencyKey);

  const pending = form.formState.isSubmitting;

  // Watched rather than kept in a second copy of state: the hint under the
  // picker and the disabled state of the switch both have to follow the value
  // the resolver will actually be given.
  const kind = form.watch('kind');
  const chosenOutletIds = form.watch('outletIds');

  const stockAllowed = mayTrackStock(kind);

  const changeKind = (next: CatalogueKind) => {
    form.setValue('kind', next);

    // Forced off rather than merely disabled. A switch left `true` behind a
    // disabled control is a value nobody can see and the server refuses,
    // which is the worst of both — the person is told off for an answer the
    // form is no longer showing them.
    if (!mayTrackStock(next)) form.setValue('tracksStock', false);
  };

  const handleSubmit = async (values: NewCatalogueItemValues) => {
    // Belt and braces with the disabled button below. A second submit carries
    // the same idempotency key and the same payload, so the API replays the
    // first answer rather than creating twice — but it would still put this
    // form into a second in-flight state for no reason.
    if (pending) return;

    setFormError(null);

    try {
      // The body's shape comes from the generated client; nothing about
      // `POST /v1/catalogue/items` is written down in this file. Both amounts
      // are converted from the rupees somebody typed to the integer paise the
      // API carries — `2500`, never `25.00` and never `'25.00'`.
      const barcode = barcodeOnTheWire(values.barcode);

      const body: CatalogueItemsController_create_v1Body = {
        kind: values.kind,
        code: values.code,
        name: values.name,
        tracksStock: values.tracksStock,
        sellingPaise: paiseFromRupees(values.sellingRupees),
        costPaise: paiseFromRupees(values.costRupees),

        // Both or two nulls, decided by the switch alone — see `tax.ts`.
        ...taxOnTheWire(values),

        // Omitted entirely rather than sent as `[]`. The two mean the same
        // thing to the API — available nowhere — and the request that appears
        // in a log should say what was asked for rather than what was defaulted.
        ...(values.outletIds.length > 0 ? { outletIds: values.outletIds } : {}),

        // Omitted when blank, for the same reason: most goods carry none (#319).
        ...(barcode === null ? {} : { barcode }),
      };

      onCreated(
        await client.request(CatalogueItemsController_create_v1, {
          body,
          headers: { [IDEMPOTENCY_KEY_HEADER]: idempotencyKey },
        }),
      );
    } catch (error) {
      if (error instanceof ApiError && error.code === 'CATALOGUE_CODE_TAKEN') {
        // Beside the field, and the focus goes with it. A 409 that names one
        // input is not a statement about the form, and somebody who submitted
        // with the keyboard is otherwise left with the caret on the button and
        // a message they have to go and find.
        form.setError('code', { message: messageForFailure(error) });
        form.setFocus('code');
      } else if (error instanceof ApiError && error.code === 'CATALOGUE_STOCK_NOT_AVAILABLE_HERE') {
        form.setError('outletIds', { message: stockRefusal(outlets, values.outletIds) });
      } else if (error instanceof ApiError && error.code === 'CATALOGUE_BARCODE_TAKEN') {
        form.setError('barcode', { message: barcodeRefusal(error, outlets) });
        form.setFocus('barcode');
      } else {
        setFormError(messageForFailure(error));
      }

      // Nothing is cleared. The retry is one corrected field, and re-typing the
      // other six to fix it is a cruelty this form is in no position to inflict.
    }
  };

  return (
    // One column, per docs/ui-conventions.md. `noValidate` because the browser's
    // own bubbles are not this app's error language and cannot be worded.
    <Form {...form}>
      <form
        className="space-y-5"
        noValidate
        onSubmit={(event) => void form.handleSubmit(handleSubmit)(event)}
      >
        <FormField
          control={form.control}
          name="kind"
          render={() => (
            <FormItem>
              <FormLabel id="catalogue-kind-label" className={fieldLabel}>
                Kind
              </FormLabel>
              {/* Not wrapped in `FormControl`: the trigger is a button rather
                  than the input the generated id belongs on, and it already
                  carries its own accessible name. The picker cannot be left
                  empty — it opens on a kind — so there is nothing here for the
                  resolver to refuse. */}
              <Select
                value={kind}
                items={CATALOGUE_KIND_ITEMS}
                // `null` is Base UI's "cleared". This picker has no affordance
                // for it — no clear button, no empty option — and `kind` is
                // required and immutable, so a cleared value is read as leaving
                // the current one alone rather than cast into the field.
                onValueChange={(next) => {
                  if (next !== null) changeKind(next);
                }}
              >
                <SelectTrigger
                  id="catalogue-kind"
                  aria-label="Kind"
                  className={`${fieldSurface} h-11 w-full`}
                >
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectGroup>
                    {CATALOGUE_KINDS.map((option) => (
                      <SelectItem key={option} value={option}>
                        {labelForKind(option)}
                      </SelectItem>
                    ))}
                  </SelectGroup>
                </SelectContent>
              </Select>
              {/* Said at the field, because this is the field it is true of.
                  There is no route that changes a kind, so the cost of getting
                  it wrong is retiring the item and creating it again under a
                  code the first one has spent for good. */}
              <FormDescription>
                {hintForKind(kind)} This cannot be changed later — an item of the wrong kind has to
                be retired and created again under a different code.
              </FormDescription>
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="code"
          render={({ field }) => (
            <FormItem>
              <FormLabel required className={fieldLabel}>
                Item code
              </FormLabel>
              <FormControl
                render={
                  <Input
                    // A code is read aloud and typed back in: mono face,
                    // tabular figures.
                    className={`${fieldSurface} h-11 font-mono tracking-wider numeric`}
                    required
                    placeholder="COLA-300"
                    autoComplete="off"
                    autoCapitalize="characters"
                    {...field}
                    onChange={(event) => {
                      // Uppercased as it is typed, because the API stores it
                      // uppercase. Left alone, somebody types `cola-300`, the
                      // list comes back saying `COLA-300`, and the screen looks
                      // like it changed their answer.
                      field.onChange(event.target.value.toUpperCase());
                    }}
                  />
                }
              />
              <FormDescription>
                Printed on receipts and tickets, and never reissued: retiring this item does not
                free its code for anything else.
              </FormDescription>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="name"
          render={({ field }) => (
            <FormItem>
              <FormLabel required className={fieldLabel}>
                Name
              </FormLabel>
              <FormControl
                render={
                  <Input
                    className={`${fieldSurface} h-11`}
                    required
                    placeholder="Cola 300ml"
                    autoComplete="off"
                    {...field}
                  />
                }
              />
              <FormDescription>What a person reads on a button and on a receipt.</FormDescription>
              <FormMessage />
            </FormItem>
          )}
        />

        <BarcodeField />

        <PriceField
          control={form.control}
          name="sellingRupees"
          label="Selling price"
          description="What a guest pays. Zero is allowed — a free child ticket is ₹0.00."
        />

        <PriceField
          control={form.control}
          name="costRupees"
          label="Cost price"
          description="What the garden pays for it. May be zero, and may be more than the selling price — loss leaders are allowed."
        />

        <TaxFields />

        {/* A quiet inset panel, and the one place the contrast trap lives:
            `text-muted-foreground` on `bg-muted` measures 4.39:1 and misses AA.
            The text here is `text-foreground` for that reason and no other. */}
        <FormField
          control={form.control}
          name="tracksStock"
          render={({ field }) => (
            <div className="flex items-start gap-3 rounded-2xl bg-muted px-4 py-3">
              <Switch
                id="tracks-stock"
                aria-labelledby="tracks-stock-label"
                className="mt-0.5"
                checked={field.value}
                disabled={!stockAllowed}
                onCheckedChange={(checked) => {
                  field.onChange(checked);
                }}
              />
              <div className="space-y-1">
                <Label
                  id="tracks-stock-label"
                  htmlFor="tracks-stock"
                  className={`text-sm text-foreground ${stockAllowed ? 'cursor-pointer' : ''}`}
                >
                  Counts stock
                </Label>
                <p className="text-sm text-foreground/70">
                  {stockAllowed
                    ? 'On for goods counted on a shelf, off for anything made to order.'
                    : `Only a product can count stock, so this is unavailable for ${labelForKind(kind).toLowerCase()} items.`}
                </p>
              </div>
            </div>
          )}
        />

        <FormField
          control={form.control}
          name="outletIds"
          render={({ field }) => (
            <FormItem>
              <FormLabel className={fieldLabel}>Sold at</FormLabel>

              {outlets.length === 0 ? (
                <FormDescription>
                  There are no outlets yet, so there is nowhere to sell this from. It can be created
                  now and made available once an outlet exists.
                </FormDescription>
              ) : (
                <>
                  {/* A fieldset rather than a `FormControl`: there is no single
                      input here for the generated id to belong to, and a reader
                      moving through the boxes needs the group named once rather
                      than "checkbox" repeated. */}
                  <div
                    role="group"
                    aria-label="Sold at"
                    className={`max-h-44 space-y-2 overflow-y-auto rounded-2xl border px-4 py-3 ${fieldSurface}`}
                  >
                    {outlets.map((outlet) => (
                      <OutletChoice
                        key={outlet.id}
                        outlet={outlet}
                        ticked={field.value.includes(outlet.id)}
                        onToggle={(next) => {
                          field.onChange(
                            next
                              ? [...field.value, outlet.id]
                              : field.value.filter((id: string) => id !== outlet.id),
                          );
                        }}
                      />
                    ))}
                  </div>
                  <FormDescription>
                    {chosenOutletIds.length === 0
                      ? 'Optional. Leave every box clear to create the item and decide where it sells later.'
                      : 'Where this can be sold from. It can be changed at any time afterwards.'}
                  </FormDescription>
                </>
              )}

              <FormMessage />
            </FormItem>
          )}
        />

        {/* Above the submit and it stays there — a toast vanishes, and somebody
            who looked away has no way to get it back. */}
        {formError !== null && (
          <Alert variant="destructive">
            <IconAlertTriangle />
            <AlertDescription>{formError}</AlertDescription>
          </Alert>
        )}

        <DialogFooter>
          <Button type="submit" disabled={pending}>
            {pending ? 'Creating…' : 'Create item'}
          </Button>
        </DialogFooter>
      </form>
    </Form>
  );
}

/**
 * A price field. Two of them, identical apart from their copy.
 *
 * Typed in rupees and sent in paise, and the `₹` is on the field rather than in
 * the value — a symbol somebody can type is a symbol the parser has to have an
 * opinion about. `inputMode="decimal"` gets the right keypad without
 * `type="number"`, which would hand the browser its own opinion about what a
 * price is: a spinner, a scroll wheel that changes the amount under a pointer,
 * and a value that reads back as `NaN` when it is empty.
 */
function PriceField({
  control,
  name,
  label,
  description,
}: {
  control: ReturnType<typeof useForm<NewCatalogueItemValues>>['control'];
  name: 'sellingRupees' | 'costRupees';
  label: string;
  description: string;
}) {
  return (
    <FormField
      control={control}
      name={name}
      render={({ field }) => (
        <FormItem>
          <FormLabel required className={fieldLabel}>
            {label}
          </FormLabel>
          <div className="relative">
            <span
              // Decorative: the label already says what this is, and a reader
              // hearing "rupees" between the label and the value gains nothing.
              aria-hidden
              className="pointer-events-none absolute top-1/2 left-3.5 -translate-y-1/2 text-muted-foreground"
            >
              ₹
            </span>
            <FormControl
              render={
                <Input
                  className={`${fieldSurface} h-11 pl-8 font-mono numeric`}
                  required
                  inputMode="decimal"
                  placeholder="25.00"
                  autoComplete="off"
                  {...field}
                />
              }
            />
          </div>
          <FormDescription>{description}</FormDescription>
          <FormMessage />
        </FormItem>
      )}
    />
  );
}

function OutletChoice({
  outlet,
  ticked,
  onToggle,
}: {
  outlet: OutletResource;
  ticked: boolean;
  onToggle: (next: boolean) => void;
}) {
  // Base UI's checkbox is a `button` with `role="checkbox"` rather than an
  // `input`, so a `<label for>` would bind to nothing. `aria-labelledby` against
  // the text that is already on screen is what names it, and the two cannot
  // disagree because there is only one of them.
  const nameId = `sold-at-${outlet.id}`;

  return (
    <div className="flex items-center gap-3">
      <Checkbox
        aria-labelledby={nameId}
        checked={ticked}
        onCheckedChange={(next) => onToggle(next === true)}
      />
      <span id={nameId} className="text-sm">
        <span className="text-foreground">{outlet.name}</span>{' '}
        <span className="font-mono text-muted-foreground numeric">{outlet.code}</span>
      </span>
    </div>
  );
}

/**
 * What to say when the API refuses the whole create because a named outlet
 * carries no stock.
 *
 * **The offending outlet is named from this form's own copy of the outlets, not
 * from the refusal.** The ticket says the server puts it in `details`; it does
 * not — `assertStockIsCarried` throws `new AppException('CATALOGUE_STOCK_NOT_
 * AVAILABLE_HERE')` with no second argument, having selected the offending id
 * and then dropped it. Reported in the PR body. Naming it from the list this
 * screen already read is honest about what is known and is the sentence somebody
 * can act on; the fallback below is what happens when this copy and the server's
 * disagree, which is a real possibility because an outlet's `carriesStock` can
 * be turned off between the two reads.
 */
function stockRefusal(outlets: readonly OutletResource[], chosen: readonly string[]): string {
  const without = outlets.filter((outlet) => chosen.includes(outlet.id) && !outlet.carriesStock);

  if (without.length === 0) {
    return 'One of the outlets chosen does not carry stock, so it cannot sell an item that counts stock. Nothing was created. Clear that outlet, or turn off Counts stock.';
  }

  const named = new Intl.ListFormat('en-IN', { type: 'conjunction' }).format(
    without.map((outlet) => outlet.name),
  );

  return `${named} ${without.length === 1 ? 'does' : 'do'} not carry stock, so ${without.length === 1 ? 'it' : 'they'} cannot sell an item that counts stock. Nothing was created. Clear ${without.length === 1 ? 'that outlet' : 'those outlets'}, or turn off Counts stock.`;
}
